import React, { useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Link } from 'react-router-dom';
import { useHelpSheet } from './HelpSheetContext';
import { useFieldHelpRegistry } from './FieldHelpRegistryContext';
import './FieldHelp.css';

// Layer 3 of the in-app help stack (see the Manual, layer 2, and the
// tooltip/helper-text layers 1-2 that stay inline in each form). A short
// answer anchored to one field, plus a deep link into the full Manual
// article for the rest. Built on Radix's Popover primitive rather than
// hand-rolled positioning/focus-trap logic.
//
// "Read the full article" opens the app-level HelpSheet (a right-side
// slider reusing ManualPage) rather than navigating to /global-manual, so
// the form underneath — often mid-edit — stays put. Falls back to a plain
// Link on the rare page that renders FieldHelp outside HelpSheetContext.
//
// `fieldKey` (e.g. "products.product-type") makes the blurb and article
// link admin-editable via Field Help Settings, same non-technical-editable
// promise Screen Help already makes for the page-level "?" — see
// FieldHelpRegistryContext. `children`/`articleSlug`/`articleTitle` stay
// the developer-authored default: they self-register the first time this
// mounts, and keep rendering as-is if fieldKey is omitted or the registry
// hasn't loaded yet. An admin override only ever wins once it's set.
//
// `label` (optional) is the Field Help Settings table's own "Field" column
// text, distinct from `articleTitle` — most fields don't need it, since the
// article they link to shares their name, but a set of fields that all
// point at one shared article (e.g. Can Buy/Sell/Pick/Track all linking to
// "Inventory Controls") would otherwise all show that same article title as
// their label and look like duplicate rows. Falls back to articleTitle,
// same as before this existed.
function FieldHelp({ fieldKey, pageKey, articleSlug, articleTitle, label, children }) {
  const helpSheet = useHelpSheet();
  const registry = useFieldHelpRegistry();
  const entry = fieldKey ? registry?.entries?.[fieldKey] : null;

  useEffect(() => {
    if (!fieldKey || !registry || typeof children !== 'string') return;
    registry.registerDefault({
      field_key: fieldKey,
      label: label || articleTitle || fieldKey,
      page_key: pageKey || null,
      default_body_text: children,
      default_article_slug: articleSlug || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  const bodyText = entry?.body_text_override || children;
  const effectiveArticleSlug = entry?.article_slug_override || articleSlug;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="field-help-trigger" aria-label="Field help">?</button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="field-help-content" sideOffset={8} align="start">
          <div className="field-help-head">Field help</div>
          <div className="field-help-body">{bodyText}</div>
          {effectiveArticleSlug && (
            helpSheet ? (
              <Popover.Close asChild>
                <button
                  type="button"
                  className="field-help-link"
                  onClick={() => helpSheet.openArticle(effectiveArticleSlug)}
                >
                  Read the full article{articleTitle ? ` — ${articleTitle}` : ''} →
                </button>
              </Popover.Close>
            ) : (
              <Link className="field-help-link" to={`/global-manual?article=${effectiveArticleSlug}`}>
                Read the full article{articleTitle ? ` — ${articleTitle}` : ''} →
              </Link>
            )
          )}
          <Popover.Arrow className="field-help-arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default FieldHelp;
